import { Card } from '@/core/ui/components';
import type { TenantDashboardUsage } from '@yawi/types';
import { Users, Building2, Database, Bell, AlertTriangle } from 'lucide-react';

interface UsageCardProps {
  usage: TenantDashboardUsage | null;
}

interface UsageBarProps {
  label: string;
  icon: React.ReactNode;
  current: number;
  max: number;
  percentage: number;
  format?: (value: number) => string;
}

function UsageBar({ label, icon, current, max, percentage, format }: UsageBarProps) {
  const isNearLimit = percentage >= 80;
  const isOverLimit = percentage >= 100;
  const formatValue = format || ((v) => v.toLocaleString());

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <span className="text-theme-secondary flex items-center gap-2">
          {icon}
          {label}
        </span>
        <span className="text-sm">
          <span className={isOverLimit ? 'text-red-600 font-medium' : 'text-theme'}>
            {formatValue(current)}
          </span>
          <span className="text-theme-secondary"> / {formatValue(max)}</span>
        </span>
      </div>
      <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${
            isOverLimit
              ? 'bg-red-500'
              : isNearLimit
              ? 'bg-amber-500'
              : 'bg-indigo-500'
          }`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
      {isNearLimit && !isOverLimit && (
        <p className="text-xs text-amber-600 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          Cerca del límite ({percentage.toFixed(0)}%)
        </p>
      )}
      {isOverLimit && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          Límite excedido ({percentage.toFixed(0)}%)
        </p>
      )}
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export function UsageCard({ usage }: UsageCardProps) {
  if (!usage) {
    return (
      <Card>
        <h3 className="text-lg font-semibold text-theme mb-4 flex items-center gap-2">
          <Database className="w-5 h-5" />
          Uso
        </h3>
        <p className="text-theme-secondary text-center py-4">
          Sin datos de uso disponibles
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <h3 className="text-lg font-semibold text-theme mb-4 flex items-center gap-2">
        <Database className="w-5 h-5" />
        Uso del período: {usage.period}
      </h3>

      <div className="space-y-5">
        <UsageBar
          label="Usuarios"
          icon={<Users className="w-4 h-4" />}
          current={usage.activeUsers}
          max={usage.usersLimit}
          percentage={usage.percentages.users}
        />

        <UsageBar
          label="Unidades"
          icon={<Building2 className="w-4 h-4" />}
          current={usage.totalUnits}
          max={usage.unitsLimit}
          percentage={usage.percentages.units}
        />

        <UsageBar
          label="Almacenamiento"
          icon={<Database className="w-4 h-4" />}
          current={usage.storageBytes}
          max={usage.storageLimitBytes}
          percentage={usage.percentages.storage}
          format={formatBytes}
        />

        <UsageBar
          label="Notificaciones"
          icon={<Bell className="w-4 h-4" />}
          current={usage.notificationsSent}
          max={usage.notificationsLimit}
          percentage={usage.notificationsLimit > 0 ? (usage.notificationsSent / usage.notificationsLimit) * 100 : 0}
        />
      </div>

      <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-theme-secondary">Emails enviados:</span>
            <span className="ml-2 text-theme font-medium">{usage.emailsSent}</span>
          </div>
          <div>
            <span className="text-theme-secondary">SMS enviados:</span>
            <span className="ml-2 text-theme font-medium">{usage.smsSent}</span>
          </div>
        </div>
      </div>
    </Card>
  );
}
